/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 2 hook(s) need dependency review; fix each and remove this directive */
import Autocomplete from "@mui/material/Autocomplete";
import type {
  AutocompleteProps as MuiAutocompleteProps,
  AutocompleteRenderOptionState,
  AutocompleteOwnerState,
  AutocompleteValue,
  AutocompleteRenderInputParams,
} from "@mui/material/Autocomplete";
import type { AutocompleteFreeSoloValueMapping } from "@mui/material/useAutocomplete";
import React, { forwardRef, isValidElement, useRef, useState, useEffect, useMemo } from "react";
import axiosWrapper from "../../services/axiosWrapper";
import ToolTipComponent from "@CubeComponents/html/tooltip";
import { untranslated } from "@i18n";

// ===== Type Definitions =====

/** Base constraint for option objects - must be a plain object with string keys */
type OptionBase = Record<string, unknown>;

/**
 * Options reach this component from callers and from `remoteDataSource` responses, neither of which
 * is validated, so a row is checked before it is indexed by name — `typeof null === "object"` makes
 * a null row reachable at runtime even though `T` cannot express one.
 */
const isOptionRecord = (option: unknown): option is OptionBase => typeof option === "object" && option !== null;

/** Reads `field` off an option row; `undefined` when the row is not an object. */
const readOptionField = (option: unknown, field: string): unknown =>
  isOptionRecord(option) ? option[field] : undefined;

/** Shape of the response from remote data source */
interface RemoteDataResponse {
  data: { result: unknown[] };
}

const defaultOptionStyle: React.CSSProperties = {
  borderTop: "1px solid #ECF3FE",
  height: "3rem",
  padding: "0 1rem",
  whiteSpace: "nowrap",
  overflow: "hidden",
  lineHeight: "3rem",
  display: "block",
  textOverflow: "ellipsis",
  transition: "background-color 300ms ease-in-out",
};

/**
 * Props for the AutoComplete component.
 *
 * Extends MUI AutocompleteProps with:
 * - `valueField` for string-to-option resolution
 * - `remoteDataSource` / `query` / `beforeDataBound` for remote data fetching
 * - `width` for convenience sizing
 *
 * @template T - Option type (object or string)
 * @template Multiple - Whether multiple values can be selected
 * @template DisableClearable - Whether the clear button is hidden
 * @template FreeSolo - Whether arbitrary string input is allowed
 */
interface AutoCompleteProps<
  T extends OptionBase | string = OptionBase,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
> extends Omit<
  MuiAutocompleteProps<T, Multiple, DisableClearable, FreeSolo>,
  "options" | "value" | "defaultValue" | "renderInput"
> {
  // ── Custom props (not in MUI) ──

  /**
   * Field name on option objects to match string values against.
   * When set, `value="abc"` resolves to `options.find(o => o[valueField] === "abc")`.
   * Only valid when options are objects (not strings).
   */
  valueField?: T extends OptionBase ? Extract<keyof T, string> : never;

  /** URL endpoint for fetching options via POST */
  remoteDataSource?: string;

  /** Query parameters sent to the remote data source */
  query?: Record<string, unknown>;

  /** Transform the API response before setting options */
  beforeDataBound?: (response: RemoteDataResponse, query: Record<string, unknown>) => T[];

  /** Convenience prop for setting the component width */
  width?: string | number;

  minWidth?: string | number;

  /** Custom styles to merge into each option's inline style */
  listStyle?: React.CSSProperties;

  // ── Overridden MUI props ──

  /** Options list — optional because remoteDataSource can provide them instead */
  options?: readonly T[];

  /**
   * The selected value. When `valueField` is set, can also be a string
   * that will be resolved against `option[valueField]`.
   */
  value?: AutocompleteValue<T, Multiple, DisableClearable, FreeSolo> | (T extends OptionBase ? string : never);

  /** Default value. When `valueField` is set, can also be a string. */
  defaultValue?: AutocompleteValue<T, Multiple, DisableClearable, FreeSolo> | (T extends OptionBase ? string : never);

  /** Render the input element (required — MUI requires it and this component provides no default) */
  renderInput: (params: AutocompleteRenderInputParams) => React.ReactNode;
}

// ===== Component Implementation =====

function AutoCompleteComponent<
  T extends OptionBase | string = OptionBase,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
>(
  props: AutoCompleteProps<T, Multiple, DisableClearable, FreeSolo>,
  ref: React.ForwardedRef<HTMLDivElement>,
): React.ReactElement {
  const btnRef = useRef<HTMLDivElement | null>(null);
  const resolvedRef = (ref || btnRef) as React.Ref<HTMLDivElement>;
  const [isLoading, setIsLoading] = useState(false);

  const {
    sx = {} as NonNullable<typeof props.sx>,
    remoteDataSource,
    query,
    options,
    beforeDataBound,
    width,
    getOptionLabel,
    renderOption,
    valueField,
    value,
    defaultValue,
    loading,
    listStyle = {},
    isOptionEqualToValue: isOptionEqualToValueProp,
    disableClearable,
    minWidth,
    ...otherProps
  } = props;

  const [optionsState, setOptionsState] = useState<T[]>(() => (Array.isArray(options) ? [...options] : []));

  useEffect(() => {
    try {
      if (Array.isArray(options)) {
        setOptionsState([...options]);
      } else if (!remoteDataSource && !query) {
        throw new Error("options should be an array or use remote data source");
      }
    } catch (e) {
      console.log(e);
      Promise.reject(e);
    }
  }, [options]);

  type MuiValue = AutocompleteValue<T, Multiple, DisableClearable, FreeSolo>;

  /**
   * MUI derives the value type from `Multiple` / `DisableClearable` / `FreeSolo`, which are still
   * unresolved type parameters here, so nothing this wrapper produces — the row matched by
   * `valueField`, `null` for "cleared", `[]` for an empty multi-select — can be *proven* to inhabit
   * `AutocompleteValue<…>`. This is the single place the wrapper claims that shape; the runtime
   * contract is MUI's own (an array when `multiple`, `null` when cleared).
   */
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- conditional type over unresolved generics; sole adopt point, see above
  const toMuiValue = (resolved: unknown): MuiValue => resolved as MuiValue;

  const valueMemo = useMemo((): MuiValue | undefined => {
    if (valueField && typeof value === "string") {
      const found = optionsState.find((o) => readOptionField(o, valueField) === value);
      return toMuiValue(found ?? null);
    }
    return value === undefined ? undefined : toMuiValue(value);
  }, [value, valueField, optionsState]);

  const defaultValueMemo = useMemo((): MuiValue | undefined => {
    if (valueField && typeof defaultValue === "string") {
      const found = optionsState.find((o) => readOptionField(o, valueField) === defaultValue);
      return toMuiValue(found ?? null);
    }
    return defaultValue === undefined ? undefined : toMuiValue(defaultValue);
  }, [defaultValue, valueField, optionsState]);

  const mountedRef = useRef(true);
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fetchOptions = async (queryParam: Record<string, unknown> | undefined = query): Promise<void> => {
    if (!queryParam || !remoteDataSource) return;
    setIsLoading(true);
    const response = await axiosWrapper.post<{ result: T[] }>(remoteDataSource, {
      value: { ...queryParam, params: queryParam },
    });
    if (!mountedRef.current) return;
    // `AxiosResponse<{ result: T[] }>` already satisfies `RemoteDataResponse` (`T[]` widens to
    // `unknown[]`), so the response is handed over as it is.
    const optionsData: T[] = beforeDataBound ? beforeDataBound(response, queryParam) : response.data.result;
    setOptionsState(optionsData);
    setIsLoading(false);
  };

  useEffect(() => {
    fetchOptions();
  }, [query]);

  type OptionOrFreeSolo = T | AutocompleteFreeSoloValueMapping<FreeSolo>;

  // MUI hands each row of `options` to these, and an unvalidated row can be nullish even though `T`
  // cannot say so — hence the wider parameter (MUI's own narrower signature still accepts it) and the
  // `unknown` reads: neither the caller's label nor an option's `label` field is trusted to be a
  // string, and this wrapper's contract is that a label is always one.
  const safeGetOptionLabel = useMemo(() => {
    if (getOptionLabel) {
      return (option: OptionOrFreeSolo | null | undefined): string => {
        if (option == null) return "";
        const label: unknown = getOptionLabel(option);
        return label == null ? "" : String(label);
      };
    }
    return (option: OptionOrFreeSolo | null | undefined): string => {
      if (option == null) return "";
      if (typeof option === "string") return option;
      const label = readOptionField(option, "label");
      return label == null ? "" : String(label);
    };
  }, [getOptionLabel]);

  const mergedOptionStyle: React.CSSProperties = useMemo(() => ({ ...defaultOptionStyle, ...listStyle }), [listStyle]);

  const resolvedRenderOption = useMemo(() => {
    return function RenderOption(
      liProps: React.HTMLAttributes<HTMLLIElement> & { key: React.Key },
      option: T,
      state: AutocompleteRenderOptionState,
      ownerState: AutocompleteOwnerState<T, Multiple, DisableClearable, FreeSolo>,
    ): React.ReactNode {
      if (renderOption) {
        const result = renderOption(liProps, option, state, ownerState);
        if (isValidElement(result) && result.type === "li") {
          return result;
        }
        return (
          <ToolTipComponent
            slotProps={{ popper: { sx: { zIndex: 10000 } } }}
            content={untranslated(typeof result === "string" ? result : "")}
          >
            <li {...liProps} style={mergedOptionStyle}>
              {result}
            </li>
          </ToolTipComponent>
        );
      }
      const content = safeGetOptionLabel(option);
      return (
        <ToolTipComponent slotProps={{ popper: { sx: { zIndex: 10000 } } }} content={untranslated(content)}>
          <li {...liProps} style={mergedOptionStyle}>
            {content}
          </li>
        </ToolTipComponent>
      );
    };
  }, [renderOption, safeGetOptionLabel, mergedOptionStyle]);

  const resolvedIsOptionEqualToValue = useMemo((): ((option: T, val: T) => boolean) | undefined => {
    if (isOptionEqualToValueProp) return isOptionEqualToValueProp;
    if (valueField) {
      return (option: T, val: T): boolean => {
        if (isOptionRecord(option) && isOptionRecord(val)) {
          return readOptionField(option, valueField) === readOptionField(val, valueField);
        }
        // Includes the null rows the old `typeof … === "object"` pair let through: two of them are
        // equal, a null and an object are not.
        return option === val;
      };
    }
    return undefined;
  }, [isOptionEqualToValueProp, valueField]);

  const isControlled = value !== undefined;

  return (
    <Autocomplete<T, Multiple, DisableClearable, FreeSolo>
      ref={resolvedRef}
      // MUI's documented merge for a wrapper that owns base styles and forwards `sx`. Spreading `sx`
      // into one object only ever worked for its plain-object form — the callback and array forms
      // have no own style keys, which is what the erased assertion was hiding.
      sx={[
        {
          minWidth: minWidth ?? "12.5rem",
          width: width || "auto",
          "& .MuiInputBase-root": { padding: "0.125rem 0.375rem", background: "white !important" },
          "& .MuiInputBase-root.Mui-disabled": { background: "#f5f5f5 !important" },
          "& .MuiInputBase-root.Mui-disabled .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(0, 0, 0, 0.26) !important",
          },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      slotProps={{ listbox: { sx: { padding: 0 } } }}
      {...otherProps}
      // `DisableClearable` is an unresolved type parameter, so no literal can be proven to inhabit
      // it; `true` is this wrapper's documented default for MUI's own boolean flag.
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- literal adopted into an unresolved generic boolean flag
      disableClearable={(disableClearable ?? true) as DisableClearable}
      {...(isControlled
        ? { value: valueMemo ?? (otherProps.multiple ? toMuiValue([]) : toMuiValue(null)) }
        : { defaultValue: defaultValueMemo })}
      loading={isLoading || loading}
      options={optionsState}
      getOptionLabel={safeGetOptionLabel}
      renderOption={resolvedRenderOption}
      {...(resolvedIsOptionEqualToValue ? { isOptionEqualToValue: resolvedIsOptionEqualToValue } : {})}
    />
  );
}

/**
 * `forwardRef` erases the component's type parameters (it returns a non-generic
 * `ForwardRefExoticComponent`), so the generic signature is restored here — the standard React idiom
 * for a generic forwardRef component. `displayName` is folded into the same declared type instead of
 * being asserted onto it a second time.
 */
// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- forwardRef drops the type parameters; see above
const AutoComplete = forwardRef(AutoCompleteComponent) as (<
  T extends OptionBase | string = OptionBase,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
>(
  props: AutoCompleteProps<T, Multiple, DisableClearable, FreeSolo> & React.RefAttributes<HTMLDivElement>,
) => React.ReactElement | null) & { displayName?: string };

AutoComplete.displayName = "AutoComplete";

export default AutoComplete;
export type { AutoCompleteProps, OptionBase };
